import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  useGetTutorialConfigQuery,
  useGetTestQuery,
  useUpdateTutorialConfigMutation,
  useDeleteTutorialConfigMutation,
} from "../../../app/services/meseeknaApiV2"
import { Information } from "../../simulation/types"
import { simContents, extractChoices, extractInformation } from "../../simulation/utils"
import {
  StepType,
  TutorialConfig,
  TutorialStep,
} from "../types/tutorial-config"
import { emptyConfig, newStep } from "./empty-config"
import GlobalsPanel from "./globals-panel"
import LivePreview from "./live-preview"
import StepEditor from "./step-editor"
import StepList, { GlobalsTab } from "./step-list"
import { errorCount, validateConfig, warningCount } from "./validation"

type Props = {
  testId: number
}

const TutorialBuilder: React.FC<Props> = ({ testId }) => {
  const {
    data: testRes,
    isLoading: testLoading,
    error: testError,
  } = useGetTestQuery(testId)
  const {
    data: configRes,
    isLoading: configLoading,
    refetch: refetchConfig,
  } = useGetTutorialConfigQuery(testId)
  const [updateConfig, { isLoading: saving }] = useUpdateTutorialConfigMutation()
  const [deleteConfig] = useDeleteTutorialConfigMutation()

  const [config, setConfig] = useState<TutorialConfig>(() => emptyConfig(testId))
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null)
  const [globalsTab, setGlobalsTab] = useState<GlobalsTab | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Hydrate config when API responds
  useEffect(() => {
    if (configLoading) return
    const incoming = configRes?.data
    if (incoming && typeof incoming === "object") {
      setConfig(incoming as TutorialConfig)
    } else {
      setConfig(emptyConfig(testId))
    }
    setDirty(false)
  }, [configRes, configLoading, testId])

  // Derive sim categories from the test's contents (same shape simContents expects)
  const categories: Information[] = useMemo(() => {
    const test = testRes?.data
    if (!test) return []
    // simContents expects { data: [...] } — wrap accordingly
    try {
      const wrapped = { data: [test] } as Parameters<typeof simContents>[0]
      const choices = extractChoices(wrapped)
      const information = extractInformation(wrapped)
      return [...choices, ...information]
    } catch (e) {
      console.warn("[TutorialBuilder] failed to extract categories", e)
      return []
    }
  }, [testRes])

  const issues = useMemo(
    () => validateConfig(config, categories),
    [config, categories],
  )
  const errors = errorCount(issues)
  const warnings = warningCount(issues)

  const updateConfigState = (next: TutorialConfig) => {
    setConfig(next)
    setDirty(true)
    setSaveMessage(null)
  }

  const patchGlobals = (patch: Partial<TutorialConfig>) =>
    updateConfigState({ ...config, ...patch })

  const selectedStep = config.steps.find((s) => s.id === selectedStepId) ?? null

  const handleAddStep = (type: StepType) => {
    const step = newStep(type)
    updateConfigState({ ...config, steps: [...config.steps, step] })
    setSelectedStepId(step.id)
    setGlobalsTab(null)
  }

  const handleStepChange = (next: TutorialStep) => {
    const updated = config.steps.map((s) => (s.id === selectedStepId ? next : s))
    // If id changed, also patch any messages that referenced the old id
    let messages = config.messages
    if (selectedStepId && next.id !== selectedStepId) {
      messages = messages.map((m) =>
        m.injectAfterStep === selectedStepId ? { ...m, injectAfterStep: next.id } : m,
      )
      setSelectedStepId(next.id)
    }
    updateConfigState({ ...config, steps: updated, messages })
  }

  const handleMoveStep = (stepId: string, direction: "up" | "down") => {
    const i = config.steps.findIndex((s) => s.id === stepId)
    if (i < 0) return
    const j = direction === "up" ? i - 1 : i + 1
    if (j < 0 || j >= config.steps.length) return
    const next = config.steps.slice()
    ;[next[i], next[j]] = [next[j], next[i]]
    updateConfigState({ ...config, steps: next })
  }

  const handleDeleteStep = (stepId: string) => {
    const remainingSteps = config.steps.filter((s) => s.id !== stepId)
    // Clean up messages that referenced this step
    const remainingMsgs = config.messages.filter(
      (m) => m.injectAfterStep !== stepId,
    )
    updateConfigState({
      ...config,
      steps: remainingSteps,
      messages: remainingMsgs,
    })
    if (selectedStepId === stepId) {
      setSelectedStepId(remainingSteps[0]?.id ?? null)
    }
  }

  const handleSelectStep = (stepId: string) => {
    setSelectedStepId(stepId)
    setGlobalsTab(null)
  }

  const handleSelectGlobalsTab = (tab: GlobalsTab) => {
    setGlobalsTab(tab)
  }

  const handleSave = async () => {
    if (errors > 0) {
      setSaveMessage(`Fix ${errors} error(s) before saving.`)
      return
    }
    try {
      await updateConfig({ testId, config }).unwrap()
      setDirty(false)
      setSaveMessage("Saved.")
      refetchConfig()
    } catch (e) {
      console.error(e)
      setSaveMessage(`Save failed: ${e instanceof Error ? e.message : "unknown"}`)
    }
  }

  const handleClear = async () => {
    if (
      !window.confirm(
        "Clear the saved tutorial config for this sim? The runtime will fall back to the bundled config (if any).",
      )
    )
      return
    try {
      await deleteConfig(testId).unwrap()
      setConfig(emptyConfig(testId))
      setDirty(false)
      setSaveMessage("Cleared.")
      refetchConfig()
    } catch (e) {
      console.error(e)
      setSaveMessage(`Clear failed: ${e instanceof Error ? e.message : "unknown"}`)
    }
  }

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(config, null, 2)], {
      type: "application/json",
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `tutorial-config-${testId}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleImportClick = () => fileInputRef.current?.click()

  const handleImportFile = async (file: File) => {
    try {
      const text = await file.text()
      const parsed = JSON.parse(text) as TutorialConfig
      if (
        !parsed ||
        typeof parsed !== "object" ||
        parsed.version !== "1.0" ||
        !Array.isArray(parsed.steps)
      ) {
        throw new Error("Not a valid tutorial config (missing version 1.0 or steps).")
      }
      updateConfigState({ ...parsed, simulationId: testId })
      setSaveMessage("Imported.")
    } catch (e) {
      window.alert(`Import failed: ${e instanceof Error ? e.message : "unknown"}`)
    }
  }

  if (testLoading || configLoading) {
    return <div className="p-6">Loading…</div>
  }
  if (testError || !testRes?.data) {
    return (
      <div className="p-6 text-red-600">
        Could not load simulation #{testId}.
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="px-4 py-2 border-b bg-white flex items-center gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold">{testRes.data.title}</div>
          <div className="text-xs text-gray-500">
            Tutorial builder · sim #{testId}
          </div>
        </div>
        <div className="text-xs">
          {errors > 0 ? (
            <span className="text-red-700 font-medium">
              {errors} error{errors === 1 ? "" : "s"}
            </span>
          ) : (
            <span className="text-green-700">No errors</span>
          )}
          {warnings > 0 && (
            <span className="text-amber-700 ml-2">
              {warnings} warning{warnings === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) handleImportFile(f)
            e.target.value = ""
          }}
        />
        <button
          type="button"
          onClick={handleImportClick}
          className="text-sm px-3 py-1 border rounded hover:bg-gray-50">
          Import JSON
        </button>
        <button
          type="button"
          onClick={handleExport}
          className="text-sm px-3 py-1 border rounded hover:bg-gray-50">
          Export JSON
        </button>
        <button
          type="button"
          onClick={handleClear}
          className="text-sm px-3 py-1 border rounded hover:bg-gray-50 text-red-600">
          Clear saved
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={errors > 0 || saving || !dirty}
          className="text-sm px-3 py-1 bg-indigo-600 text-white rounded disabled:opacity-50">
          {saving ? "Saving…" : dirty ? "Save" : "Saved"}
        </button>
      </div>
      {saveMessage && (
        <div className="px-4 py-1 text-xs bg-gray-100 border-b text-gray-700">
          {saveMessage}
        </div>
      )}

      {/* Three-pane layout */}
      <div
        className="grid flex-1 overflow-hidden"
        style={{ gridTemplateColumns: "240px 1fr 380px" }}>
        <div className="border-r overflow-hidden">
          <StepList
            config={config}
            selectedStepId={selectedStepId}
            selectedGlobalsTab={globalsTab}
            onSelectStep={handleSelectStep}
            onSelectGlobalsTab={handleSelectGlobalsTab}
            onAddStep={handleAddStep}
            onMoveStep={handleMoveStep}
            onDeleteStep={handleDeleteStep}
          />
        </div>
        <div className="overflow-y-auto bg-white">
          {globalsTab ? (
            <GlobalsPanel
              tab={globalsTab}
              config={config}
              categories={categories}
              onChange={patchGlobals}
            />
          ) : selectedStep ? (
            <StepEditor
              step={selectedStep}
              config={config}
              issues={issues}
              onChange={handleStepChange}
            />
          ) : (
            <div className="p-6 text-sm text-gray-500">
              Select a step on the left or pick a Globals tab to begin editing.
            </div>
          )}
        </div>
        <div className="border-l overflow-hidden bg-gray-50">
          <LivePreview config={config} categories={categories} testId={testId} />
        </div>
      </div>
    </div>
  )
}

export default TutorialBuilder
